import type React from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { client } from "@duress/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api-client";
import { AlertDialog } from "@/components/ui/alert-dialog";
import {
  useClientNotes,
  useCreateNote,
  useDeleteNote,
  useUpdateNote,
} from "../useClientNotes";

type EditingState =
  | { mode: "create" }
  | { mode: "edit"; note: client.ClientNote }
  | null;

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const FIELD_KEYS = ["title", "date", "body"] as const;
type FieldKey = (typeof FIELD_KEYS)[number];

export function ClientNotesTab({ clientId }: { clientId: string }): React.JSX.Element {
  const { data, isLoading, error } = useClientNotes(clientId);
  const [editing, setEditing] = useState<EditingState>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const notes: client.ClientNote[] = data?.items ?? [];

  return (
    <div className="max-w-2xl space-y-4">
      {editing == null ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1.5"
          onClick={() => { setEditing({ mode: "create" }); }}
        >
          <Plus className="h-3.5 w-3.5" />
          Add note
        </Button>
      ) : (
        <NoteForm
          clientId={clientId}
          editing={editing}
          onCancel={() => { setEditing(null); }}
          onSaved={() => { setEditing(null); }}
        />
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : error ? (
        <p className="text-sm text-destructive">Couldn't load notes. Try again.</p>
      ) : notes.length === 0 ? (
        <p className="text-sm text-muted-foreground">No notes yet — add one above.</p>
      ) : (
        <ul className="space-y-2">
          {notes.map((note) => (
            <NoteRow
              key={note.id}
              clientId={clientId}
              note={note}
              expanded={expandedId === note.id}
              onToggle={() => { setExpandedId((id) => (id === note.id ? null : note.id)); }}
              onEdit={() => { setEditing({ mode: "edit", note }); setExpandedId(null); }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function NoteRow({
  clientId,
  note,
  expanded,
  onToggle,
  onEdit,
}: {
  clientId: string;
  note: client.ClientNote;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
}): React.JSX.Element {
  const del = useDeleteNote(clientId);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleConfirmDelete = async (): Promise<void> => {
    await del.mutateAsync(note.id);
    setDeleteOpen(false);
  };

  const preview = note.body.length > 120
    ? note.body.slice(0, 120).trimEnd() + "…"
    : note.body;

  return (
    <li className="rounded-md border border-border bg-card">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-3 py-2 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-md"
        aria-expanded={expanded}
      >
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-medium text-sm truncate">{note.title}</span>
          <span className="text-xs text-muted-foreground shrink-0">{note.date}</span>
        </div>
        {!expanded && note.body ? (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{preview}</p>
        ) : null}
      </button>
      {expanded ? (
        <div className="px-3 pb-3 space-y-3">
          {note.body ? (
            <p className="text-sm whitespace-pre-wrap">{note.body}</p>
          ) : (
            <p className="text-sm text-muted-foreground italic">(no body)</p>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={onEdit}
              disabled={del.isPending}
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => { setDeleteOpen(true); }}
              disabled={del.isPending}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          </div>
        </div>
      ) : null}
      <AlertDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={`Delete "${note.title}"?`}
        description="This note will be permanently deleted and can't be recovered."
        confirmLabel="Delete note"
        onConfirm={() => void handleConfirmDelete()}
        isPending={del.isPending}
      />
    </li>
  );
}

function NoteForm({
  clientId,
  editing,
  onCancel,
  onSaved,
}: {
  clientId: string;
  editing: Exclude<EditingState, null>;
  onCancel: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const create = useCreateNote(clientId);
  const update = useUpdateNote(clientId);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const defaults: client.CreateClientNoteRequest =
    editing.mode === "edit"
      ? { title: editing.note.title, date: editing.note.date, body: editing.note.body }
      : { title: "", date: todayIso(), body: "" };

  const form = useForm<client.CreateClientNoteRequest>({
    resolver: zodResolver(client.CreateClientNoteRequestSchema),
    defaultValues: defaults,
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      if (editing.mode === "create") {
        await create.mutateAsync(values);
      } else {
        await update.mutateAsync({ noteId: editing.note.id, input: values });
      }
      onSaved();
    } catch (err) {
      const handled = applyServerErrors(err, (field, message) => {
        form.setError(field, { message });
      });
      if (!handled) {
        setSubmitError(err instanceof Error ? err.message : "Save failed.");
      }
    }
  });

  const pending = create.isPending || update.isPending;

  return (
    <form
      onSubmit={(e) => void onSubmit(e)}
      className="space-y-3 rounded-md border border-border bg-muted/20 p-3"
    >
      <div className="space-y-1.5">
        <Label htmlFor="note-title">Title</Label>
        <Input id="note-title" type="text" maxLength={200} {...form.register("title")} />
        {form.formState.errors.title ? (
          <p className="text-xs text-destructive">{form.formState.errors.title.message}</p>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="note-date">Date</Label>
        <Input id="note-date" type="date" {...form.register("date")} className="w-44" />
        {form.formState.errors.date ? (
          <p className="text-xs text-destructive">{form.formState.errors.date.message}</p>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="note-body">Body</Label>
        <textarea
          id="note-body"
          rows={5}
          maxLength={10_000}
          {...form.register("body")}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
        {form.formState.errors.body ? (
          <p className="text-xs text-destructive">{form.formState.errors.body.message}</p>
        ) : null}
      </div>

      {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={pending}>
          {pending ? "Saving…" : editing.mode === "create" ? "Add note" : "Save"}
        </Button>
      </div>
    </form>
  );
}

function applyServerErrors(
  err: unknown,
  setError: (field: FieldKey, message: string) => void,
): boolean {
  if (!(err instanceof ApiError) || !err.body.errors) return false;
  let matched = false;
  for (const issue of err.body.errors) {
    if (isFieldKey(issue.field)) {
      setError(issue.field, issue.message ?? issue.code);
      matched = true;
    }
  }
  return matched;
}

function isFieldKey(field: string): field is FieldKey {
  return (FIELD_KEYS as readonly string[]).includes(field);
}
